import { useEffect, useRef, useState } from 'react';

// All visible activity uses one monotonic deadline. A reply never gains a new
// freshness window simply because it arrives late or its object is replaced.
export const useEffectiveActivity = (activity, clock, challengeId) => {
  const observation = useRef(null);
  const [, tick] = useState(0);
  const now = performance.now();
  if (observation.current?.activity !== activity || observation.current?.clock !== clock || observation.current?.challengeId !== challengeId) {
    const checked = Date.parse(activity?.checked_at);
    const expiry = Date.parse(activity?.expires_at);
    const freshUntil = Date.parse(activity?.fresh_until);
    const requestAge = Number.isFinite(clock?.requestStarted) ? now - clock.requestStarted : 0;
    const serverAge = Number.isFinite(clock?.responseDate) ? clock.responseDate - checked + now - clock.receivedAt : 0;
    // A fast local clock can conservatively end a display early, never late.
    const age = Math.max(0, requestAge, Number.isFinite(serverAge) ? serverAge : 0, Date.now() - checked || 0);
    const expiryDeadline = Number.isFinite(checked) && Number.isFinite(expiry) ? now + expiry - checked - age : -Infinity;
    const phaseDeadline = Number.isFinite(checked) && Number.isFinite(freshUntil) ? now + freshUntil - checked - age : -Infinity;
    const phaseKey = `${activity?.recorded_at}|${activity?.fresh_until}`;
    const previous = observation.current;
    const sameChallenge = previous?.challengeId === challengeId && previous?.activity?.state === 'in_progress';
    observation.current = { activity, clock, challengeId, phaseKey, timingValid: Number.isFinite(checked) && Number.isFinite(expiry),
      expiryDeadline: sameChallenge ? Math.min(previous.expiryDeadline, expiryDeadline) : expiryDeadline,
      phaseDeadline: sameChallenge && previous.phaseKey === phaseKey ? Math.min(previous.phaseDeadline, phaseDeadline) : phaseDeadline };
  }
  useEffect(() => {
    if (activity?.state !== 'in_progress') return;
    const timer = window.setInterval(() => tick(value => value + 1), 250);
    return () => window.clearInterval(timer);
  }, [activity?.state]);
  const rawState = activity?.state || 'not_started';
  const visibleState = rawState === 'in_progress' && !observation.current.timingValid ? 'unverified'
    : rawState === 'in_progress' && now >= observation.current.expiryDeadline ? 'window_ended' : rawState;
  const fresh = visibleState === 'in_progress' && activity?.freshness === 'fresh' && now < observation.current.phaseDeadline;
  return { visibleState, fresh, phase: fresh ? activity.phase : 'unknown' };
};